import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Car, Clock, CheckCircle, XCircle, RefreshCw, DollarSign, Loader2, FileText, FilePlus } from "lucide-react";
import { getMyDeals, DealRecord } from "@/Calls/callDeals";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { socket } from "@/lib/socket";
import { DealSocketPayload } from "@/types/socketEvents";

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: React.ElementType }> = {
    PENDING:  { label: "Pending Review", color: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400",  icon: Clock },
    VIEWED:   { label: "Viewed by Dealer", color: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",    icon: RefreshCw },
    COUNTERED:{ label: "Counter Offer",    color: "bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-400", icon: RefreshCw },
    ACCEPTED: { label: "Accepted!",        color: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400", icon: CheckCircle },
    DECLINED: { label: "Declined",         color: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",        icon: XCircle },
    FUNDED:   { label: "Funded",           color: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400", icon: CheckCircle },
    CANCELLED:{ label: "Cancelled",        color: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",       icon: XCircle },
};

function fmt(v: string | null | undefined) {
    if (!v) return "—";
    const n = Number(v);
    if (isNaN(n)) return v;
    return `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function DealCard({ deal }: { deal: DealRecord }) {
    const status = deal.deal_status ?? "PENDING";
    const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.PENDING;
    const Icon = cfg.icon;
    const isFinance = (deal.deal_type ?? "FINANCE") === "FINANCE";

    return (
        <Card className="p-5 space-y-4">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <p className="font-medium">
                        {deal.vYear} {deal.vMake} {deal.vModel}
                        {deal.vTrim ? ` ${deal.vTrim}` : ""}
                    </p>
                    {deal.vVin && (
                        <p className="text-xs text-muted-foreground font-mono mt-0.5">VIN: {deal.vVin}</p>
                    )}
                </div>
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.color}`}>
                    <Icon className="h-3 w-3" />
                    {cfg.label}
                </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                <Stat label="Vehicle Price" value={fmt(deal.priceVehicle)} />
                {isFinance ? (
                    <>
                        <Stat label="Monthly Payment" value={deal.monthlypymts ? `${fmt(deal.monthlypymts)}/mo` : "—"} highlight />
                        <Stat label="Amount Financed" value={fmt(deal.amountDDue)} />
                        <Stat label="Term" value={deal.term ? `${deal.term} mo` : "—"} />
                        <Stat label="APR" value={deal.apr ? `${deal.apr}%` : "—"} />
                    </>
                ) : (
                    <Stat label="Cash Due" value={fmt(deal.amountDDue)} highlight />
                )}
                {deal.downPayment && Number(deal.downPayment) > 0 && (
                    <Stat label="Down Payment" value={fmt(deal.downPayment)} />
                )}
            </div>

            <div className="flex items-center justify-between pt-1 border-t">
                <span className="text-xs text-muted-foreground">
                    {isFinance ? "Finance" : "Cash"} · Submitted {new Date(deal.deal_created_at).toLocaleDateString()}
                </span>
                <span className="text-xs text-muted-foreground font-mono">#{deal.deal_number}</span>
            </div>

            {status === "COUNTERED" && (
                <div className="rounded-lg bg-violet-50 dark:bg-violet-950/30 border border-violet-200 dark:border-violet-800 p-3 text-sm">
                    <p className="font-medium text-violet-800 dark:text-violet-300 mb-1">Dealer Counter Offer</p>
                    <p className="text-violet-700 dark:text-violet-400">
                        New payment: <strong>{deal.monthlypymts ? `${fmt(deal.monthlypymts)}/mo` : "—"}</strong>
                        {deal.amountDDue ? ` · Financed: ${fmt(deal.amountDDue)}` : ""}
                        {deal.apr ? ` · APR: ${deal.apr}%` : ""}
                        {deal.term ? ` · ${deal.term} mo` : ""}
                    </p>
                </div>
            )}

            {status === "ACCEPTED" && (
                <div className="rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 p-3 text-sm text-emerald-800 dark:text-emerald-300">
                    Your deal was accepted! The dealer will contact you to finalize paperwork.
                </div>
            )}

            <div className="flex flex-wrap gap-2 pt-1 border-t">
                <Link to="/customer/documents">
                    <Button size="sm" variant="outline">
                        <FileText className="h-3.5 w-3.5 mr-1.5" />
                        View Documents
                    </Button>
                </Link>
                {isFinance && (
                    <Link to={`/customer/credit-app?dealId=${deal.deal_id}`}>
                        <Button size="sm" variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50">
                            <FilePlus className="h-3.5 w-3.5 mr-1.5" />
                            Apply for Financing
                        </Button>
                    </Link>
                )}
            </div>
        </Card>
    );
}

function Stat({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
    return (
        <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
            <p className={`font-medium mt-0.5 ${highlight ? "text-indigo-600 dark:text-indigo-400" : ""}`}>{value}</p>
        </div>
    );
}

export default function CustomerDealsPage() {
    const { user } = useAuth();
    const [deals, setDeals] = useState<DealRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [liveIds, setLiveIds] = useState<Set<number>>(new Set());

    // Fetch all customer deals — no dealer filter, server returns by customer_id
    // For now we fetch all and filter client-side by the customer session.
    // In production a GET /deals/mine endpoint would be cleaner.
    useEffect(() => {
        (async () => {
            try {
                const data = await getMyDeals();
                setDeals(data);
            } catch {
                // silent
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    // Real-time status updates from dealer
    useEffect(() => {
        if (!user) return;
        socket.emit("registerCustomer", { customerId: user.id });

        const onUpdate = (payload: DealSocketPayload) => {
            setDeals(prev =>
                prev.map(d =>
                    d.deal_id === payload.dealId
                        ? {
                            ...d,
                            deal_status: payload.status,
                            monthlypymts: payload.monthlyPayment ? String(payload.monthlyPayment) : d.monthlypymts,
                            amountDDue: payload.amountFinanced ? String(payload.amountFinanced) : d.amountDDue,
                        }
                        : d
                )
            );
            // Flash highlight
            setLiveIds(prev => new Set(prev).add(payload.dealId));
            setTimeout(() => setLiveIds(prev => { const s = new Set(prev); s.delete(payload.dealId); return s; }), 3000);
        };

        socket.on("dealStatusUpdate", onUpdate);
        return () => { socket.off("dealStatusUpdate", onUpdate); };
    }, [user]);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-24">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
        );
    }

    return (
        <div className="max-w-3xl mx-auto space-y-6">
            <div>
                <h1 className="text-2xl font-light uppercase tracking-wide">My Deals</h1>
                <p className="text-muted-foreground text-sm mt-1">
                    Real-time deal status from your dealer. Updates arrive instantly.
                </p>
            </div>

            {deals.length === 0 ? (
                <div className="text-center py-20 space-y-4">
                    <DollarSign className="h-12 w-12 mx-auto text-muted-foreground/30" />
                    <p className="text-muted-foreground">No deals submitted yet.</p>
                    <Link
                        to="/customer/dashboard"
                        className="inline-block text-sm text-indigo-600 hover:underline"
                    >
                        Browse vehicles to start a deal
                    </Link>
                </div>
            ) : (
                <div className="space-y-4">
                    {deals.map(deal => (
                        <div
                            key={deal.deal_id}
                            className={`transition-all ${liveIds.has(deal.deal_id) ? "ring-2 ring-indigo-400 rounded-xl" : ""}`}
                        >
                            <DealCard deal={deal} />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
